import axios from "axios";
import store from "../store/store";
import { logout } from "../store/slices/authSlice";
import toast from "react-hot-toast";
import { requestDeduplication, createRequestKey } from "../lib/requestDeduplication";
import { showComplianceBlocked, showComplianceWarnings } from "../features/compliance/utils/complianceNotices";

// Set the API base URL globally. Default is local backend, can be overridden by REACT_APP_API_URL.
// Set the API base URL globally. Default is local backend, can be overridden by REACT_APP_API_URL.
const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_URL ||
    "http://localhost:8000",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Required for HttpOnly cookies
});

// Helper to get CSRF token from cookies
const getCookie = (name) => {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

// Local memory cache for CSRF token (fallback for cross-domain)
let memCsrfToken = null;

// Refresh lock — prevents concurrent 401s from triggering multiple simultaneous refresh calls
let isRefreshing = false;
let refreshQueue = []; // callbacks waiting for the refresh to complete

const processRefreshQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

// Initial CSRF fetch function
export const ensureCsrfToken = async () => {
  if (memCsrfToken) return memCsrfToken;
  try {
    const baseURL = api.defaults.baseURL.replace(/\/$/, '');
    const response = await axios.get(`${baseURL}/api/csrf/`, { withCredentials: true });
    memCsrfToken = response.data.csrfToken;
    return memCsrfToken;
  } catch (e) {
    console.warn("Failed to fetch CSRF token", e);
    return null;
  }
};

// Request interceptor for CSRF, Authorization header fallback, and Deduplication
api.interceptors.request.use(
  (config) => {
    // 1. Attach CSRF token if available (only for non-safe methods)
    const isSafeMethod = ['get', 'head', 'options'].includes(config.method?.toLowerCase());
    if (!isSafeMethod) {
      const csrfToken = getCookie('csrftoken') || memCsrfToken;
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      }
    }

    // 2. Authorization header fallback for iOS/Safari (WebKit blocks HttpOnly cookies on XHR).
    //    Always overwrite so retried requests pick up the latest token after a refresh.
    const storedToken = localStorage.getItem('jwt_access');
    if (storedToken) {
      config.headers['Authorization'] = `Bearer ${storedToken}`;
    }

    // 3. Request deduplication
    const requestKey = createRequestKey(config.url, {
      method: config.method,
      params: config.params,
      data: config.data
    });
    config.requestKey = requestKey;

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (response.config.requestKey) {
      requestDeduplication.clear(response.config.requestKey);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest && originalRequest.requestKey) {
      requestDeduplication.clear(originalRequest.requestKey);
    }

    // Handle network errors (backend not running)
    if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error' || error.code === 'ERR_CONNECTION_REFUSED')) {
      const apiUrl = api.defaults.baseURL;
      console.error(`❌ Network Error: Cannot connect to backend at ${apiUrl}`);
      console.error('This usually means:');
      console.error('1. Backend server is not running');
      console.error('2. Backend is running on a different port');
      console.error('3. CORS configuration issue');
      console.error(`Check your .env file for REACT_APP_API_URL (currently: ${apiUrl})`);
      
      // Don't show toast for every failed request, just log it
      // The component will handle showing the error message
    }

    // Handle 401 Unauthorized - Attempt Token Refresh
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Avoid infinite loops for login/refresh endpoints
      if (originalRequest.url.includes('login') || originalRequest.url.includes('token/refresh')) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      const baseURL = api.defaults.baseURL.replace(/\/$/, '');

      // If a refresh is already in progress, queue this request until it finishes.
      // This prevents concurrent 401s from firing multiple simultaneous refresh calls,
      // which causes the rotated refresh token to be invalidated on the second attempt.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        }).then(() => api(originalRequest))
          .catch(err => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${baseURL}/api/token/refresh/`,
          {},
          { withCredentials: true }
        );

        // Update stored token for Authorization header fallback (iOS/Safari WebKit)
        if (refreshResponse.data?.access) {
          localStorage.setItem('jwt_access', refreshResponse.data.access);
        }

        isRefreshing = false;
        processRefreshQueue(null);

        // Retry original request — interceptor will attach the fresh token automatically
        return api(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processRefreshQueue(refreshError);
        store.dispatch(logout());
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// Create a deduplicated version of the API
const createDeduplicatedApi = (apiInstance) => {
  const deduplicatedApi = {};

  // Wrap GET requests with deduplication
  deduplicatedApi.get = (url, config = {}) => {
    const requestKey = createRequestKey(url, {
      method: 'GET',
      params: config.params
    });

    return requestDeduplication.execute(requestKey, () => {
      return apiInstance.get(url, config);
    });
  };

  // POST, PUT, DELETE should NOT be deduplicated (they modify data)
  deduplicatedApi.post = apiInstance.post.bind(apiInstance);
  deduplicatedApi.put = apiInstance.put.bind(apiInstance);
  deduplicatedApi.patch = apiInstance.patch.bind(apiInstance);
  deduplicatedApi.delete = apiInstance.delete.bind(apiInstance);

  return deduplicatedApi;
};

// Wording shared by every "couldn't reach the backend" toast in the app. The
// server is essentially never actually down (it's a production Heroku app,
// not something a field agent's phone could affect) - the real cause is
// almost always the agent's own mobile connection having a slow moment, so
// telling them to go "check if the Django server is running" is both wrong
// and something they have no way to act on (agent report, 2026-08-14/15:
// scared a field agent out of using the app during a live open home over
// what was just a slow request).
export const NETWORK_ERROR_MESSAGE = "Having trouble reaching the server. Check your connection and try again.";

// Backend connection check utility. A single slow response (a real
// possibility on mobile data, especially at a property with poor signal)
// used to trip this after just 5 seconds - loosened to 15s, and retried once
// before actually reporting a problem, so one slow moment doesn't read as an
// outage.
const pingBackend = (baseURL, timeout) =>
  axios.get(`${baseURL}/api/csrf/`, { withCredentials: true, timeout });

export const checkBackendConnection = async () => {
  const baseURL = api.defaults.baseURL.replace(/\/$/, '');
  for (let attempt = 1; attempt <= 2; attempt += 1) {
    try {
      await pingBackend(baseURL, 15000);
      return {
        connected: true,
        url: baseURL,
        message: 'Backend is accessible'
      };
    } catch (error) {
      if (attempt === 2) {
        return {
          connected: false,
          url: baseURL,
          error: error.message || 'Connection failed',
          message: NETWORK_ERROR_MESSAGE
        };
      }
      // One retry, immediately - filters out a single transient blip.
    }
  }
};

// Export both regular and deduplicated API
// Property compliance gates: surface warn / block answers from any endpoint
// (property edit, visibility toggles, REA publish, eSign send, contract emails).
api.interceptors.response.use(
  (response) => {
    showComplianceWarnings(response?.data);
    return response;
  },
  (error) => {
    showComplianceBlocked(error);
    return Promise.reject(error);
  }
);

export default api;
export const deduplicatedApi = createDeduplicatedApi(api);

export const searchProperties = (params = {}) =>
  deduplicatedApi.get("property-management/property-search/", { params });

// Export Public API functions (for authenticated or non-authenticated public endpoints)
export * from "./publicApi";

// Export Twilio API functions
export * from "./twilio";

// Export Task API functions
export * from "./task";

// Export KPI API functions
export * from "./kpi";

// Export Auto Replies API functions
export * from "./cannedResponse";

// Export REA API functions
export * from "./rea";

// Export Contact & Buyer Requirements API functions
export * from "./contact";

// Export Inspection/OFI API functions
export * from "./inspection";

// Export Agent/Property API functions
export * from "./agent";